import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { ISettingCropType } from 'common/defines/clients';
import { TypeRaster } from 'common/defines/constants';
import { HexColorPickerDebounce } from 'common/utils/HexColorPickerDebounce';
import AvatarUser from 'components/Common/AvatarUser';
import DropFileCustom from 'components/Common/DropFile';
import SkeletonCommon from 'components/Common/SkeletonCommon';
import configs from 'constants/config';
import { QUERY_KEY, STATUS_API } from 'constants/constants';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  createCropTypeSettings,
  getSettingsDataCroptypeById,
  updateCropTypeSettings,
} from 'services/settings/settings.services';
import { useCropTypeSettingDialogStyles } from '../CropTypeDialogSettingStyle';
import AnalysisTypeArea from './AnalysisTypeArea';

const UserAnalysisDialogCropType = (props: any) => {
  const queryClient = useQueryClient();
  const { searchSettings, onClose, page, rowsPerPage, settingIndex, editData, typeAnalysisOptions } = props;
  const [avaShow, setAvaShow] = useState('');
  const [openPickColor, setOpenPickColor] = useState(false);
  const [document, setDocument] = useState(null);
  const [documentEdit, setDocumentEdit] = useState<any>(undefined);
  const theme = useTheme();
  const token = localStorage.getItem('token');

  const form = useForm<ISettingCropType>({
    defaultValues: {
      icon: '',
      pdfDocument: '',
      name: '',
      color: theme.palette.primary.main,
      analyticsDisplay: [],
      analysisConfig: [],
      isAutoCreateAnalysisRGB: [],
      isAutoCreateAnalysisRgbMs: [],
      isAutoCreateAnalysisErms: [],
      rgbList: [],
      rgbMsList: [],
      ermsList: [],
    },
  });

  const { control, handleSubmit, reset, setValue, getValues } = form;

  // Call API
  const { data, isLoading, remove, status } = useQuery([QUERY_KEY.USER_SETTINGS_CROPTYPE_BY_ID, settingIndex], () =>
    getSettingsDataCroptypeById(settingIndex)
  );

  const dataSettingsAnalyticsValue = useMemo(() => {
    return data?.data;
  }, [data]);

  useEffect(() => {
    if (settingIndex) {
      if (status !== STATUS_API.SUCCESS) return;
      const arrayRgb = [] as any[];
      const arrayRgbList = [] as any[];
      const arrayErmsList = [] as any[];
      const arrayRgbCheck = [] as any[];
      const arrayRgbMsCheck = [] as any[];
      const arrayErmsCheck = [] as any[];

      dataSettingsAnalyticsValue?.analysisConfig.forEach((item: any) => {
        if (item.rasterType === TypeRaster.RGB) {
          arrayRgb.push(item.name);
          if (item.isAutoCreateAnalysis) arrayRgbCheck.push(item.name);
        } else if (item.rasterType === TypeRaster.RGB_MULTI_SPECTRAL) {
          arrayRgbList.push(item.name);
          if (item.isAutoCreateAnalysis) arrayRgbMsCheck.push(item.name);
        } else if (item.rasterType === TypeRaster.ERMS) {
          arrayErmsList.push(item.name);
          if (item.isAutoCreateAnalysis) arrayErmsCheck.push(item.name);
        }
      });

      setAvaShow(dataSettingsAnalyticsValue.icon);
      // setValue('isAutoCreateAnalysis', dataSettingsAnalyticsValue?.isAutoCreateAnalysis);
      setValue('icon', dataSettingsAnalyticsValue?.icon);
      setValue('name', dataSettingsAnalyticsValue?.name);
      setValue('color', dataSettingsAnalyticsValue?.color);
      setValue('analyticsDisplay', dataSettingsAnalyticsValue?.analyticsDisplay);
      setValue('analysisConfig', dataSettingsAnalyticsValue?.analysisConfig);
      setValue('rgbList', arrayRgb);
      setValue('rgbMsList', arrayRgbList);
      setValue('ermsList', arrayErmsList);
      setValue('isAutoCreateAnalysisRGB', arrayRgbCheck);
      setValue('isAutoCreateAnalysisRgbMs', arrayRgbMsCheck);
      setValue('isAutoCreateAnalysisErms', arrayErmsCheck);
      setValue('pdfDocument', dataSettingsAnalyticsValue?.pdfDocument);
      const nameFileRegex = dataSettingsAnalyticsValue?.pdfDocument?.replace(/^.*[\\\/-]/, '');
      if (dataSettingsAnalyticsValue?.pdfDocument) {
        let fileConvertFromUrl = new File([dataSettingsAnalyticsValue.pdfDocument], decodeURI(nameFileRegex) || '');
        if (fileConvertFromUrl) {
          setDocumentEdit(fileConvertFromUrl);
          return;
        }
      }
      setDocumentEdit(null);
    } else {
      reset({
        icon: '',
        name: '',
        color: theme.palette.primary.main,
        // isAutoCreateAnalysis: false,
        analyticsDisplay: [],
        analysisConfig: [],
        isAutoCreateAnalysisRGB: [],
        isAutoCreateAnalysisRgbMs: [],
        isAutoCreateAnalysisErms: [],
        rgbList: [],
        rgbMsList: [],
        ermsList: [],
        pdfDocument: '',
      });
    }
  }, [dataSettingsAnalyticsValue, reset, setValue, settingIndex, status]);

  const handleChangeField = (event: ChangeEvent<HTMLInputElement>, fieldName: keyof ISettingCropType) => {
    const trimInputValue = event.target.value.replace(/\s+/g, ' ');
    setValue(fieldName, trimInputValue);
  };

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    let value = '';
    const file = event.target.files;
    if (!file) return;
    const newFile = file[0];
    if (newFile.type === 'image/svg+xml') (newFile as any).preview = URL.createObjectURL(newFile);
    else toast.error('Please upload svg file!', { toastId: 1 });
    const formData = new FormData();
    formData.append('file', newFile);

    setAvaShow((newFile as any).preview);
    fetch(`${configs.API_DOMAIN}/media/uploadGeneral`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })
      .then((response) => response.json())
      .then((result) => {
        const isSvg = result.path.includes('.svg');
        value = isSvg ? result.path : null;

        setValue('icon', value);
        return value;
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (!editData) return;
    reset({
      icon: editData.icon,
      name: editData.name,
      color: editData.color,
      // isAutoCreateAnalysis: editData.isAutoCreateAnalysis,
      analyticsDisplay: editData.analyticsDisplay,
    });
  }, [editData, reset]);

  const handleClose = () => {
    setAvaShow('');
    reset({
      icon: '',
      name: '',
      color: theme.palette.primary.main,
      // isAutoCreateAnalysis: false,
      analyticsDisplay: [],
      isAutoCreateAnalysisRGB: [],
      isAutoCreateAnalysisRgbMs: [],
      isAutoCreateAnalysisErms: [],
      rgbList: [],
      rgbMsList: [],
      ermsList: [],
      analysisConfig: [],
      pdfDocument: '',
    });
    setDocument(null);
    setAvaShow('');
    setDocumentEdit(null);

    onClose();
  };

  useEffect(() => {
    return () => {
      reset({
        icon: '',
        name: '',
        color: theme.palette.primary.main,
        // isAutoCreateAnalysis: false,
        analyticsDisplay: [],
        isAutoCreateAnalysisRGB: [],
        isAutoCreateAnalysisRgbMs: [],
        isAutoCreateAnalysisErms: [],
        rgbList: [],
        rgbMsList: [],
        ermsList: [],
        analysisConfig: [],
        pdfDocument: '',
      });

      setDocumentEdit(undefined);
      remove();
    };
  }, [remove, reset]);

  // Call API Post
  const useCreateCropTypeSettings = () => {
    return useMutation(
      (param: ISettingCropType) =>
        createCropTypeSettings({
          ...param,
        }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY.USER_SETTINGS_CROPTYPE, page, rowsPerPage, searchSettings]);
          toast.success('Create setting successfully', { toastId: 1 });
          setAvaShow('');
          reset({
            icon: '',
            name: '',
            color: theme.palette.primary.main,
            // isAutoCreateAnalysis: false,
            analyticsDisplay: [],
            isAutoCreateAnalysisRGB: [],
            isAutoCreateAnalysisRgbMs: [],
            isAutoCreateAnalysisErms: [],
            rgbList: [],
            rgbMsList: [],
            ermsList: [],
            analysisConfig: [],
          });
          onClose();
        },
        onError: (result: any) => {
          if (result.status === 400) {
            toast.error(result.data.message[0], { toastId: 1 });
          }
        },
      }
    );
  };
  // Call API Patch
  const useUpdateCropTypeSettings = () => {
    return useMutation(
      (param: ISettingCropType) =>
        updateCropTypeSettings({
          ...param,
          id: settingIndex,
        }),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEY.USER_SETTINGS_CROPTYPE, page, rowsPerPage, searchSettings]);
          toast.success('Update setting successfully', { toastId: 1 });
          setAvaShow('');
          reset({
            icon: '',
            name: '',
            color: theme.palette.primary.main,
            analyticsDisplay: [],
            isAutoCreateAnalysisRGB: [],
            isAutoCreateAnalysisRgbMs: [],
            isAutoCreateAnalysisErms: [],
            rgbList: [],
            rgbMsList: [],
            ermsList: [],
            analysisConfig: [],
          });
          onClose();
        },
        onError: (result: any) => {
          if (result.status === 400) {
            toast.error(result.data.message[0], { toastId: 1 });
          }
        },
      }
    );
  };

  const mutationUpdateCropTypeSettings = useUpdateCropTypeSettings();
  const mutationCreateCropTypeSettings = useCreateCropTypeSettings();

  // Submit Form
  const handleSubmitForm: SubmitHandler<ISettingCropType> = (data: any) => {
    const array = [] as any;

    data.rgbList.forEach((item: any) => {
      array.push({
        name: item,
        rasterType: TypeRaster.RGB,
        isAutoCreateAnalysis: data.isAutoCreateAnalysisRGB.some((i: any) => i === item) ? true : false,
      });
    });

    data.rgbMsList?.forEach((item: any) => {
      array.push({
        name: item,
        rasterType: TypeRaster.RGB_MULTI_SPECTRAL,
        isAutoCreateAnalysis: data.isAutoCreateAnalysisRgbMs.some((i: any) => i === item) ? true : false,
      });
    });

    data.ermsList?.forEach((item: any) => {
      array.push({
        name: item,
        rasterType: TypeRaster.ERMS,
        isAutoCreateAnalysis: data.isAutoCreateAnalysisErms.some((i: any) => i === item) ? true : false,
      });
    });

    const { icon, name, color, analyticsDisplay, pdfDocument } = data;

    const dataBody = {
      icon,
      name,
      color,
      analyticsDisplay,
      analysisConfig: array,
      pdfDocument,
    };

    if (settingIndex) {
      // Update
      mutationUpdateCropTypeSettings.mutate({
        ...dataBody,
      });
    } else {
      // Create
      mutationCreateCropTypeSettings.mutate(dataBody);
    }
  };

  const classes = useCropTypeSettingDialogStyles();

  const handleClickColorPicker = () => {
    setOpenPickColor(!openPickColor);
  };

  const handleChangeDocument = (event: any) => {
    if (!event) {
      setValue('pdfDocument', '');
    }
    setDocumentEdit(event);
    setDocument(event);
    if (event) {
      const formData = new FormData();
      formData.append('file', event);

      // setAvaShow((newFile as any).preview);
      fetch(`${configs.API_DOMAIN}/media/uploadGeneral`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })
        .then((response) => response.json())
        .then((result) => {
          result && setValue('pdfDocument', result.path);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth open={true} onClose={handleClose} className={classes.dialogContainer}>
      {settingIndex ? (
        <>
          {isLoading ? (
            <>
              <DialogTitle
                classes={{
                  root: classes.dialogTitle,
                }}
                sx={{
                  background: theme.palette.background.paper,
                }}>
                Update Type
              </DialogTitle>
              <Box
                className={classes.loadingScreen}
                sx={{
                  background: theme.palette.background.paper,
                }}>
                <CircularProgress />
              </Box>
            </>
          ) : (
            <Scrollbars autoHide style={{ height: '750px' }}>
              <DialogTitle
                className={classes.dialogTitle}
                sx={{
                  background: theme.palette.background.paper,
                }}>
                Update Type
              </DialogTitle>
              {/* Container */}
              <Box
                className={classes.dialogContent}
                sx={{ paddingBottom: '16px', background: theme.palette.background.paper }}>
                {/* Form */}
                <Box component="form" className={classes.formContainer} onSubmit={handleSubmit(handleSubmitForm)}>
                  <DialogContent className={classes.formBlock}>
                    <Box className={classes.formBlockPickup}>
                      <Grid container>
                        <Grid item xs={6}>
                          <Controller
                            name="icon"
                            control={control}
                            render={({ field }) => (
                              <Box
                                className={classes.formBlockItem}
                                sx={{
                                  flex: 1,
                                }}>
                                <InputLabel htmlFor="cropTypeIcon">Type Icon</InputLabel>
                                <AvatarUser
                                  avatar={avaShow}
                                  handleChangeFile={(e) => handleChangeFile(e)}
                                  heightAvatar="50px"
                                  widthAvatar="50px"
                                  heightBtnUpload="30px"
                                  btnLabel="Upload Icon"
                                  backgroundColor={getValues('color')}
                                />
                              </Box>
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Controller
                            name="color"
                            control={control}
                            render={({ field }) => (
                              <Box
                                className={classes.formBlockItem}
                                sx={{
                                  flex: 1,
                                  position: 'relative',
                                }}>
                                <InputLabel htmlFor="cropTypeIcon">Type Color</InputLabel>
                                <Box onClick={() => handleClickColorPicker()} className={classes.pickupColorBox}>
                                  <Typography
                                    className={classes.pickupColorValue}
                                    sx={{
                                      color: getValues('color'),
                                      border: `1px solid ${getValues('color')}`,
                                    }}>
                                    {getValues('color') === theme.palette.primary.main ? 'Default' : getValues('color')}
                                  </Typography>
                                  <Tooltip
                                    title={openPickColor ? 'Close pick color' : 'Open pick color'}
                                    placement="right"
                                    arrow>
                                    <Box
                                      className={classes.pickupColorBtn}
                                      sx={{
                                        backgroundColor: getValues('color'),
                                      }}
                                    />
                                  </Tooltip>
                                </Box>
                                {
                                  // <ColorPicker
                                  openPickColor ? (
                                    <Box className={classes.pickupColorValueContainer}>
                                      <TextField
                                        className={classes.editColor}
                                        id="cropTypeColor"
                                        type="text"
                                        size="small"
                                        fullWidth
                                        {...field}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeField(e, 'color')}
                                      />
                                      <HexColorPickerDebounce
                                        {...field}
                                        style={{
                                          position: 'absolute',
                                          top: '0px',
                                          boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.2)',
                                          borderRadius: '10px',
                                        }}
                                        color={getValues('color')}
                                        onChange={(color: any) => {
                                          setValue('color', color);
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <></>
                                  )
                                }
                              </Box>
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <Box className={classes.formBlockItem}>
                          <InputLabel htmlFor="cropTypeName">Type Name</InputLabel>
                          <TextField
                            id="cropTypeName"
                            type="text"
                            size="small"
                            fullWidth
                            {...field}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeField(e, 'name')}
                          />
                        </Box>
                      )}
                    />
                    <Box className="rgb-card">
                      <AnalysisTypeArea form={form} typeAnalysisOptions={typeAnalysisOptions} />
                    </Box>

                    {status !== STATUS_API.SUCCESS || documentEdit === undefined ? (
                      <SkeletonCommon />
                    ) : (
                      <Grid item xs={12} sx={{ pb: 2 }}>
                        <DropFileCustom
                          fileSelected={documentEdit}
                          acceptFile="pdf"
                          labelFileSelected="Document"
                          subLabelTypeFile="Upload your document file here..."
                          onSelectFile={handleChangeDocument}
                        />
                      </Grid>
                    )}
                  </DialogContent>
                  <DialogActions className={classes.dialogActions}>
                    <Button
                      type="submit"
                      fullWidth
                      className={classes.submitBtnForm}
                      data-testid="next-btn"
                      variant="contained">
                      {settingIndex ? 'Update Type' : 'Create Type'}
                    </Button>
                  </DialogActions>
                </Box>
              </Box>
            </Scrollbars>
          )}
        </>
      ) : (
        <Scrollbars autoHide style={{ height: '750px' }}>
          <DialogTitle
            className={classes.dialogTitle}
            sx={{
              background: theme.palette.background.paper,
            }}>
            Create Type
          </DialogTitle>
          {/* Container */}
          <Box
            className={classes.dialogContent}
            sx={{
              background: theme.palette.background.paper,
            }}>
            {/* Form */}
            <Box component="form" className={classes.formContainer} onSubmit={handleSubmit(handleSubmitForm)}>
              <DialogContent className={classes.formBlock}>
                <Box className={classes.formBlockPickup} sx={{ paddingBottom: '16px' }}>
                  <Grid container>
                    <Grid item xs={6}>
                      <Controller
                        name="icon"
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.formBlockItemAva}>
                            <InputLabel htmlFor="cropTypeIcon">Type Icon</InputLabel>
                            <AvatarUser
                              avatar={avaShow}
                              handleChangeFile={(e) => handleChangeFile(e)}
                              heightAvatar="50px"
                              widthAvatar="50px"
                              heightBtnUpload="30px"
                              btnLabel="Upload Icon"
                              backgroundColor={getValues('color')}
                            />
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Controller
                        name="color"
                        control={control}
                        render={({ field }) => (
                          <Box className={classes.formBlockItemColor}>
                            <InputLabel htmlFor="cropTypeIcon">Type Color</InputLabel>
                            <Box onClick={() => handleClickColorPicker()} className={classes.pickupColorBox}>
                              <Typography
                                className={classes.pickupColorValue}
                                sx={{
                                  color: getValues('color'),
                                  border: `1px solid ${getValues('color')}`,
                                }}>
                                {getValues('color') === theme.palette.primary.main ? 'Default' : getValues('color')}
                              </Typography>
                              <Tooltip
                                title={openPickColor ? 'Close pick color' : 'Open pick color'}
                                placement="right"
                                arrow>
                                <Box
                                  className={classes.pickupColorBtn}
                                  sx={{
                                    backgroundColor: getValues('color'),
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            {
                              // <ColorPicker
                              openPickColor ? (
                                <Box className={classes.pickupColorValueContainer}>
                                  <TextField
                                    className={classes.editColor}
                                    id="cropTypeColor"
                                    type="text"
                                    size="small"
                                    fullWidth
                                    {...field}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeField(e, 'color')}
                                  />
                                  <HexColorPickerDebounce
                                    {...field}
                                    style={{
                                      position: 'absolute',
                                      top: '0px',
                                      boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.2)',
                                      borderRadius: '10px',
                                    }}
                                    color={getValues('color')}
                                    onChange={(color: any) => {
                                      setValue('color', color);
                                    }}
                                  />
                                </Box>
                              ) : (
                                <></>
                              )
                            }
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Box className={classes.formBlockItem}>
                      <InputLabel htmlFor="cropTypeName">Type Name</InputLabel>
                      <TextField
                        id="cropTypeName"
                        type="text"
                        size="small"
                        fullWidth
                        {...field}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeField(e, 'name')}
                      />
                    </Box>
                  )}
                />
                <AnalysisTypeArea form={form} typeAnalysisOptions={typeAnalysisOptions} />
                <Grid item xs={12}>
                  <DropFileCustom
                    fileSelected={document}
                    acceptFile="pdf"
                    labelFileSelected="Document"
                    subLabelTypeFile="Upload your document file here..."
                    onSelectFile={handleChangeDocument}
                  />
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  type="submit"
                  fullWidth
                  className={classes.submitBtnForm}
                  data-testid="next-btn"
                  variant="contained">
                  {settingIndex ? 'Update Type' : 'Create Type'}
                </Button>
              </DialogActions>
            </Box>
          </Box>
        </Scrollbars>
      )}
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          padding: 0,
          position: 'absolute',
          right: 8,
          top: 8,
          '.MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          color: (theme) => theme.palette.color.grey5,
        }}>
        <CloseIcon />
      </IconButton>
    </Dialog>
  );
};

export default UserAnalysisDialogCropType;
